/**
 * The Assistant's conversation (vNext-api `docs/copilot.md`).
 *
 * - The conversation is read when the workbench opens and whenever the
 *   case's version moves (every Assistant write is a case event) — and every
 *   few seconds while a reply runs that this page isn't streaming: another
 *   analyst's, or one whose stream dropped and couldn't be re-attached.
 * - A message sent here streams its reply: text as it's written, each tool,
 *   task, ask and proposal (`applyEvent`, the API's `TurnSink` mirrored). A
 *   dropped stream re-attaches from the last event it saw — the turn runs
 *   on regardless — and where it can't (another replica), the poll shows
 *   the reply once it's saved.
 * - Someone else's running reply is followed the same way.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';

import { askCopilot, CopilotStreamError, followCopilot } from '@/services/copilotStream';
import { vnextApiClient } from '@/services/vnext-api';
import type {
  CopilotBlock,
  CopilotConversation,
  CopilotEvent,
  CopilotMessage,
  CopilotProposal,
  CopilotSelection,
} from '@/types/copilot';
import { toError } from '@/utils/errors';
import { UNCHANGED, usePoll, withEtag } from './usePoll';

/** How often the conversation is read while a reply runs unseen here. */
export const COPILOT_RUNNING_POLL_MS = 4_000;
/** Re-attaching to a dropped stream: tries before falling back to polling. */
const REATTACH_TRIES = 2;
/** Tools whose outcome is its own block: no chip unless refused. */
const OWN_BLOCKS = new Set(['case.invoke_specialist', 'case.propose', 'case.ask']);

/** A reply streaming here: ours, or one we attached to. */
export interface LiveReply {
  /** The analyst's message as sent here — shown until the conversation has it. */
  asked: { text: string; selection: CopilotSelection | null } | null;
  askedId: string | null;
  messageId: string | null;
  blocks: CopilotBlock[];
  proposals: CopilotProposal[];
  /** `thinking` · `writing` · `done` · `error`. */
  state: string;
  lastSeq: number;
  error: string | null;
  /** Where the current model step's text began (`TurnSink.step`). */
  mark: [number, number];
}

export function startReply(asked: LiveReply['asked'], messageId: string | null = null): LiveReply {
  return {
    asked,
    askedId: null,
    messageId,
    blocks: [],
    proposals: [],
    state: 'thinking',
    lastSeq: 0,
    error: null,
    mark: [0, 0],
  };
}

export function isFinal(reply: LiveReply): boolean {
  return reply.state === 'done' || reply.state === 'error';
}

/** One event applied to a streaming reply, as the API's sink keeps it. */
export function applyEvent(reply: LiveReply, event: CopilotEvent): LiveReply {
  const next: LiveReply = { ...reply, lastSeq: Math.max(reply.lastSeq, event.seq) };
  const blocks = [...reply.blocks];
  const last = blocks.at(-1);
  switch (event.event) {
    case 'turn':
      return { ...next, messageId: event.data.messageId, askedId: event.data.askedId };
    case 'status':
      if (event.data.state !== 'thinking') {
        return { ...next, state: event.data.state };
      }
      return {
        ...next,
        state: 'thinking',
        mark: last?.type === 'text' ? [blocks.length - 1, last.text.length] : [blocks.length, 0],
      };
    case 'text':
      if (!event.data.delta) {
        return next;
      }
      if (last?.type === 'text') {
        blocks[blocks.length - 1] = { ...last, text: last.text + event.data.delta };
      } else {
        blocks.push({ type: 'text', text: event.data.delta });
      }
      return { ...next, blocks, state: 'writing' };
    case 'reset': {
      const [index, length] = reply.mark;
      const kept = blocks.slice(0, index + 1);
      const at = kept[index];
      if (at?.type === 'text') {
        const text = at.text.slice(0, length);
        if (text) {
          kept[index] = { ...at, text };
        } else {
          kept.splice(index, 1);
        }
      }
      return { ...next, blocks: kept };
    }
    case 'tool': {
      const { data } = event;
      if (OWN_BLOCKS.has(data.name) && (data.state === 'running' || data.state === 'ok')) {
        return next;
      }
      const at = blocks.findIndex((b) => b.type === 'tool' && b.callId === data.callId);
      if (at === -1) {
        blocks.push({ type: 'tool', ...data });
      } else {
        blocks[at] = { type: 'tool', ...data };
      }
      return { ...next, blocks };
    }
    case 'task':
      return { ...next, blocks: [...blocks, { type: 'task', ...event.data }] };
    case 'ask':
      return { ...next, blocks: [...blocks, { type: 'ask', ...event.data }] };
    case 'proposal': {
      const { proposal } = event.data;
      return {
        ...next,
        blocks: [
          ...blocks,
          {
            type: 'proposal',
            proposalId: proposal.id,
            kind: proposal.kind,
            summary: proposal.summary,
          },
        ],
        proposals: [...reply.proposals.filter((p) => p.id !== proposal.id), proposal],
      };
    }
    case 'done':
      return {
        ...next,
        state: 'done',
        blocks: event.data.message?.blocks ?? blocks,
      };
    case 'error':
      return {
        ...next,
        state: 'error',
        error: event.data.message ?? event.data.reason,
        blocks: event.data.reply?.blocks ?? blocks,
      };
    default:
      return next;
  }
}

/** The conversation as shown: its messages, a streaming reply in its
 * place — and, until the conversation has them, the message just sent and
 * its reply at the end. */
export function shownMessages(
  conversation: CopilotConversation | null,
  live: LiveReply | null
): CopilotMessage[] {
  const messages = [...(conversation?.messages ?? [])];
  if (!live) {
    return messages;
  }
  const status = live.state === 'error' ? 'failed' : live.state === 'done' ? 'done' : 'running';
  const at = messages.findIndex((m) => m.id === live.messageId);
  if (at !== -1) {
    messages[at] = {
      ...messages[at],
      blocks: live.blocks,
      status,
      error: live.error ?? messages[at].error,
    };
    return messages;
  }
  const threadId = conversation?.thread?.id ?? '';
  const seq = (messages.at(-1)?.seq ?? 0) + 1;
  if (live.asked && !messages.some((m) => m.id === live.askedId)) {
    messages.push({
      id: live.askedId ?? 'asking',
      threadId,
      seq,
      role: 'analyst',
      kind: 'turn',
      replyToId: null,
      author: '',
      text: live.asked.text,
      selection: live.asked.selection,
      blocks: [],
      status: 'done',
      error: null,
      model: null,
      modelCalls: 0,
      costUsd: '0',
      createdAt: null,
    });
  }
  messages.push({
    id: live.messageId ?? 'replying',
    threadId,
    seq: seq + 1,
    role: 'copilot',
    kind: 'turn',
    replyToId: live.askedId,
    author: 'copilot',
    text: '',
    selection: null,
    blocks: live.blocks,
    status,
    error: live.error,
    model: null,
    modelCalls: 0,
    costUsd: '0',
    createdAt: null,
  });
  return messages;
}

/** Why a message wasn't taken: busy (whose turn), at the threshold, not here. */
export interface CopilotRefusal {
  reason: string;
  message: string;
  runningBy: string | null;
}

export interface UseCopilot {
  conversation: CopilotConversation | null;
  error: Error | null;
  messages: CopilotMessage[];
  proposals: Map<string, CopilotProposal>;
  live: LiveReply | null;
  streaming: boolean;
  refusal: CopilotRefusal | null;
  notice: string | null;
  clearNotice: () => void;
  send: (text: string, selection: CopilotSelection | null) => Promise<boolean>;
  stop: () => Promise<void>;
  newConversation: () => Promise<void>;
  apply: (proposalId: string) => Promise<void>;
  dismiss: (proposalId: string) => Promise<void>;
  /** Several applied together: one new draft for the edits, one run. */
  applyMany: (proposalIds: string[]) => Promise<void>;
  refetch: () => Promise<void>;
}

export function useCopilot(
  reportId: string,
  version: string | null,
  /** After an applied proposal or a stopped reply: the case moved. */
  onChanged?: () => void | Promise<void>
): UseCopilot {
  const [live, setLive] = useState<LiveReply | null>(null);
  const [streaming, setStreaming] = useState(false);
  const [refusal, setRefusal] = useState<CopilotRefusal | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const streamingRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);

  const poll = usePoll<CopilotConversation>({
    enabled: version !== null,
    interval: (data) => (data?.running && !streamingRef.current ? COPILOT_RUNNING_POLL_MS : null),
    refetchDeps: [reportId, version],
    queryFn: async ({ signal, etag, manual }) => {
      const result = await vnextApiClient.getCopilot(reportId, null, {
        signal,
        etag: manual ? null : etag,
      });
      return result === 'not-modified' ? UNCHANGED : withEtag(result.data, result.etag);
    },
    onError: (err) => Sentry.captureException(toError(err)),
  });
  const { refetch, setData } = poll;
  const conversation = poll.data ?? null;

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    []
  );

  /** Stream events into `reply` until it ends: the first stream, then
   * re-attaches from the last event seen; false when it couldn't follow
   * the reply to its end (the poll takes over). */
  const run = useCallback(
    async (
      first: (onEvent: (event: CopilotEvent) => void, signal: AbortSignal) => Promise<void>,
      reply: LiveReply
    ): Promise<boolean> => {
      const controller = new AbortController();
      abortRef.current = controller;
      streamingRef.current = true;
      setStreaming(true);
      let current = reply;
      setLive(current);
      const onEvent = (event: CopilotEvent) => {
        current = applyEvent(current, event);
        setLive(current);
      };
      try {
        await first(onEvent, controller.signal);
        for (let tries = 0; !isFinal(current) && current.messageId && tries < REATTACH_TRIES; ) {
          tries += 1;
          try {
            await followCopilot(
              reportId,
              current.messageId,
              current.lastSeq,
              onEvent,
              controller.signal
            );
          } catch (err) {
            if (controller.signal.aborted) {
              return false;
            }
            if (err instanceof CopilotStreamError && err.status === 404) {
              break;
            }
          }
        }
        return isFinal(current);
      } finally {
        streamingRef.current = false;
        setStreaming(false);
        if (!controller.signal.aborted) {
          await refetch();
          setLive(null);
        }
      }
    },
    [reportId, refetch]
  );

  const send = useCallback(
    async (text: string, selection: CopilotSelection | null): Promise<boolean> => {
      if (streamingRef.current) {
        return false;
      }
      setRefusal(null);
      setNotice(null);
      try {
        await run(
          (onEvent, signal) => askCopilot(reportId, text, selection, onEvent, signal),
          startReply({ text, selection })
        );
        return true;
      } catch (err) {
        if (err instanceof CopilotStreamError && err.status === 409) {
          const running = (err.detail?.running ?? null) as { by?: string } | null;
          setRefusal({
            reason: String(err.detail?.reason ?? 'refused'),
            message: err.message,
            runningBy: running?.by ?? null,
          });
          return false;
        }
        Sentry.captureException(toError(err));
        setNotice(`Not sent: ${toError(err).message}`);
        return false;
      }
    },
    [reportId, run]
  );

  // Someone else's reply running (or ours, from another tab): follow it.
  const runningId = conversation?.running?.messageId ?? null;
  useEffect(() => {
    if (!runningId || streamingRef.current) {
      return;
    }
    void run(
      (onEvent, signal) => followCopilot(reportId, runningId, 0, onEvent, signal),
      startReply(null, runningId)
    ).catch(() => {
      // Not running in this process: the poll shows it once it's saved.
    });
  }, [reportId, runningId, run]);

  const stop = useCallback(async () => {
    const id = live?.messageId ?? conversation?.running?.messageId;
    if (!id) {
      return;
    }
    try {
      await vnextApiClient.stopCopilot(reportId, id);
      await onChanged?.();
    } catch (err) {
      setNotice(`Couldn't stop it: ${toError(err).message}`);
    }
  }, [conversation?.running?.messageId, live?.messageId, onChanged, reportId]);

  const newConversation = useCallback(async () => {
    try {
      await vnextApiClient.newCopilotThread(reportId);
      setRefusal(null);
      await refetch();
    } catch (err) {
      setNotice(toError(err).message);
    }
  }, [reportId, refetch]);

  const settled = useCallback(
    (proposal: CopilotProposal) => {
      setData((prev) =>
        prev
          ? {
              ...prev,
              proposals: [...prev.proposals.filter((p) => p.id !== proposal.id), proposal],
            }
          : prev
      );
      setLive((prev) =>
        prev
          ? { ...prev, proposals: prev.proposals.map((p) => (p.id === proposal.id ? proposal : p)) }
          : prev
      );
    },
    [setData]
  );

  const decide = useCallback(
    async (proposalId: string, how: 'apply' | 'dismiss') => {
      setNotice(null);
      try {
        const proposal =
          how === 'apply'
            ? await vnextApiClient.applyCopilotProposal(reportId, proposalId)
            : await vnextApiClient.dismissCopilotProposal(reportId, proposalId);
        settled(proposal);
        if (proposal.status === 'stale') {
          setNotice(
            `Not applied — the case moved since it was proposed${
              proposal.error ? `: ${proposal.error}` : ''
            }.`
          );
        }
        await onChanged?.();
      } catch (err) {
        setNotice(`Not ${how === 'apply' ? 'applied' : 'dismissed'}: ${toError(err).message}`);
        await refetch();
      }
    },
    [onChanged, refetch, reportId, settled]
  );

  const applyMany = useCallback(
    async (proposalIds: string[]) => {
      setNotice(null);
      try {
        const settledAll = await vnextApiClient.applyCopilotProposals(reportId, proposalIds);
        settledAll.forEach(settled);
        const stale = settledAll.filter((p) => p.status === 'stale');
        if (stale.length > 0) {
          setNotice(
            `${stale.length} of ${settledAll.length} not applied — the draft moved since they were proposed: ${stale
              .map((p) => p.error)
              .filter(Boolean)
              .join('; ')}`
          );
        }
        await onChanged?.();
      } catch (err) {
        setNotice(`Not applied: ${toError(err).message}`);
        await refetch();
      }
    },
    [onChanged, refetch, reportId, settled]
  );

  const proposals = new Map<string, CopilotProposal>();
  for (const proposal of [...(live?.proposals ?? []), ...(conversation?.proposals ?? [])]) {
    proposals.set(proposal.id, proposal);
  }

  return {
    conversation,
    error: poll.error === undefined ? null : toError(poll.error),
    messages: shownMessages(conversation, live),
    proposals,
    live,
    streaming,
    refusal,
    notice,
    clearNotice: () => setNotice(null),
    send,
    stop,
    newConversation,
    apply: (id) => decide(id, 'apply'),
    dismiss: (id) => decide(id, 'dismiss'),
    applyMany,
    refetch,
  };
}
